/**
 * ChannelNotesTab — the WIRED reservation "channel notes" tab body (SCRUM-382).
 *
 * Displays the operator-supplied Channel Reference Notes for a manually-recorded
 * ChannelCollect booking: the channel's gross guest payment, OTA commission, and
 * channel-remitted taxes, plus a free-text note. A thin renderer over
 * `useReservationChannelNotes` — no new primitives, no barrel widening.
 *
 * **Provenance honesty (the crux, §user-guide L3 / SCRUM-382).** These figures
 * were ENTERED MANUALLY, not delivered by the channel — the header states exactly
 * that. The provenance is `operator-supplied`; the UI NEVER labels them "from
 * channel". This is the honest counterpart to the channel-transcription
 * `asReported` ledger.
 *
 * **Strictly informational.** A persistent note states the figures are for
 * reference only — not part of the ledger, not re-priced, guest balance stays $0.
 *
 * **Honest states.** Loading skeleton; error + Retry (`refetch`); forbidden →
 * honest access note; `notFound` → honest empty ("No channel reference notes
 * recorded") — a `notFound` is NOT an error, no Retry.
 *
 * **Money is `Cents` (FE rule §6).** Every figure is a server passthrough;
 * `formatCents(cents, intl)` runs at the display boundary only. A `null` figure
 * renders the neutral "—" marker, never `formatCents(0)`.
 *
 * **Tokens / A11y.** Semantic `var(--token)` arbitrary-value classes only;
 * semantic `<dl>` for the figures; status conveyed by text; axe-clean light+dark.
 */

import * as React from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@sparta/ui-kit';
import { formatCents } from '../../../shared/formatters/entity-change/values.js';
import {
  useReservationChannelNotes,
  type ChannelReferenceNotesBody,
} from '../api/use-reservation-channel-notes.js';

export interface ChannelNotesTabProps {
  /** The reservation whose channel reference notes to render (`/reservations/:id`). */
  readonly reservationId: string;
}

export function ChannelNotesTab({ reservationId }: ChannelNotesTabProps): React.ReactElement {
  const notes = useReservationChannelNotes(reservationId);

  if (notes.status === 'loading') {
    return <TabSkeleton />;
  }
  if (notes.status === 'forbidden') {
    return (
      <EmptyState
        title="You don’t have access to these notes."
        body="Ask an administrator to grant you the required permission."
      />
    );
  }
  if (notes.status === 'notFound') {
    return (
      <EmptyState
        title="No channel reference notes recorded."
        body="Channel reference notes are optional figures an operator can record when manually entering a channel booking."
      />
    );
  }
  if (notes.status === 'error') {
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="flex items-center justify-between gap-4"
      >
        <div>
          <p className="m-0 text-sm font-semibold text-[color:var(--fg-primary)]">
            Couldn’t load channel reference notes.
          </p>
          <p className="m-0 text-sm text-[color:var(--fg-secondary)]">
            Check your connection and try again.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={notes.refetch}>
          Retry
        </Button>
      </div>
    );
  }

  return <NotesBody data={notes.data} />;
}

/* ── ready body ──────────────────────────────────────────────────────────── */

function NotesBody({ data }: { readonly data: ChannelReferenceNotesBody }): React.ReactElement {
  const intl = useIntl();
  const money = (cents: number | null): string =>
    cents === null ? '—' : formatCents(cents, intl);

  return (
    <div className="flex flex-col gap-5">
      {/* Provenance-honest header: entered manually, informational only. */}
      <div className="rounded-xl border border-solid border-[var(--border-default)] bg-[var(--bg-sunken)] p-3 text-sm text-[color:var(--fg-secondary)]">
        <span className="font-semibold text-[color:var(--fg-primary)]">Entered manually.</span>{' '}
        These channel-reported figures were recorded by an operator for reference — they were not
        delivered by the channel. They are informational only: not part of the ledger, not
        re-priced, and the guest balance stays $0.
      </div>

      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Figure label="Gross guest payment" value={money(data.grossGuestPaymentCents)} />
        <Figure label="OTA commission" value={money(data.otaCommissionCents)} />
        <Figure label="Channel-remitted taxes" value={money(data.channelRemittedTaxCents)} />
      </dl>

      {data.note !== null ? (
        <div className="flex flex-col gap-1">
          <h3 className="m-0 text-xs font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]">
            Note
          </h3>
          <p className="m-0 whitespace-pre-wrap text-sm text-[color:var(--fg-primary)]">
            {data.note}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function Figure({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3">
      <dt className="text-xs font-medium text-[color:var(--fg-tertiary)]">{label}</dt>
      <dd className="m-0 tabular-nums text-base font-semibold text-[color:var(--fg-primary)]">
        {value}
      </dd>
    </div>
  );
}

/* ── shared honest states ────────────────────────────────────────────────── */

function EmptyState({
  title,
  body,
}: {
  readonly title: string;
  readonly body: string;
}): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <p className="text-sm font-semibold text-[color:var(--fg-primary)]">{title}</p>
      <p className="mt-1 max-w-md text-sm text-[color:var(--fg-secondary)]">{body}</p>
    </div>
  );
}

function TabSkeleton(): React.ReactElement {
  return (
    <div aria-busy="true" aria-live="polite" data-testid="channel-notes-skeleton">
      <span
        className="mb-4 inline-block h-12 w-full rounded bg-[var(--bg-sunken)]"
        aria-hidden="true"
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="inline-block h-16 w-full rounded bg-[var(--bg-sunken)]"
            aria-hidden="true"
          />
        ))}
      </div>
      <span className="sr-only">Loading channel reference notes…</span>
    </div>
  );
}
